import { useState, useEffect, useCallback } from 'react';
import api from '../lib/axios';

// =============================================
// HOOK DE PRÉSENCE EN LIGNE
// Gère l'état de présence des utilisateurs
// =============================================

const PRESENCE_UPDATE_INTERVAL = 30000; // 30 seconds
const PRESENCE_TIMEOUT = 60000; // 1 minute - user considered offline after this

export const usePresence = (projectId, userId) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Update presence on the server
  const updatePresence = useCallback(async () => {
    if (!projectId || !userId) return;
    
    try {
      await api.post(`/projects/${projectId}/presence`, {
        user_id: userId,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      // Silently fail - presence is not critical
      console.debug('Presence update failed:', error);
    }
  }, [projectId, userId]);

  // Fetch online users
  const fetchOnlineUsers = useCallback(async () => {
    if (!projectId) return;
    
    try {
      const response = await api.get(`/projects/${projectId}/presence`);
      const users = response.data || [];
      
      // Filter users that were active in the last PRESENCE_TIMEOUT
      const now = Date.now();
      const activeUsers = users.filter(u => {
        const lastSeen = new Date(u.last_seen).getTime();
        return (now - lastSeen) < PRESENCE_TIMEOUT;
      });
      
      setOnlineUsers(activeUsers);
    } catch (error) {
      // Return empty array on error
      setOnlineUsers([]);
    }
  }, [projectId]);

  // Track user activity
  const trackActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Set up intervals and event listeners
  useEffect(() => {
    if (!projectId || !userId) return;

    // Initial update
    updatePresence();
    fetchOnlineUsers();

    // Set up periodic updates
    const presenceInterval = setInterval(() => {
      updatePresence();
      fetchOnlineUsers();
    }, PRESENCE_UPDATE_INTERVAL);

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, trackActivity);
    });

    return () => {
      clearInterval(presenceInterval);
      events.forEach(event => {
        window.removeEventListener(event, trackActivity);
      });
    };
  }, [projectId, userId, updatePresence, fetchOnlineUsers, trackActivity]);

  return {
    onlineUsers,
    isOnline: (checkUserId) => onlineUsers.some(u => u.user_id === checkUserId),
    onlineCount: onlineUsers.length,
    lastActivity
  };
};

export default usePresence;
